import { Injectable, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HotToastService } from '@ngxpert/hot-toast';
import { PLATFORM_TYPE, WINDOW } from '@tvd/cdk/browser';
import { DialogService } from '@tvd/design-system/dialog';
import { AuthService } from '@tvd/data-access/auth';
import { fromEvent, map, startWith } from 'rxjs';

const PWA_TOAST_SHOWN_KEY = 'zyn_pwa_toast_shown';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaService {
  readonly #window = inject(WINDOW);
  readonly #toast = inject(HotToastService);
  readonly #auth = inject(AuthService);
  readonly #dialog = inject(DialogService);

  readonly #installPrompt = signal<BeforeInstallPromptEvent | null>(null);

  // Detect if running on iOS Safari
  readonly #isIOSDevice = this.#detectIOS();

  // Track if app is already installed via display-mode
  readonly #standaloneQuery = this.#window.matchMedia(
    '(display-mode: standalone)',
  );
  readonly #isStandalone = toSignal(
    fromEvent<MediaQueryListEvent>(this.#standaloneQuery, 'change').pipe(
      map((e) => e.matches),
      startWith(this.#standaloneQuery.matches),
    ),
    { initialValue: this.#standaloneQuery.matches },
  );

  // App is installable if we have a prompt AND not already installed
  readonly isInstallable = signal(false);
  readonly isIOS = signal(this.#isIOSDevice);

  constructor() {
    // Native Capacitor builds are already an installed app — suppress all
    // PWA install affordances. iOS standalone detection would otherwise
    // trigger the "Add to Home Screen" toast inside the native shell.
    if (inject(PLATFORM_TYPE) === 'mobile') {
      this.isIOS.set(false);
      return;
    }

    // Listen for beforeinstallprompt event
    this.#window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.#installPrompt.set(e as BeforeInstallPromptEvent);
      this.#updateInstallableState();
    });

    // Listen for app installed event
    this.#window.addEventListener('appinstalled', () => {
      this.#installPrompt.set(null);
      this.#updateInstallableState();
    });

    // Update installable state when standalone mode changes
    effect(() => {
      this.#isStandalone();
      this.#updateInstallableState();
    });

    // Show auto-toast when installable (once per user)
    effect(() => {
      if (this.isInstallable() && this.#auth.loggedIn()) {
        this.#showAutoToast();
      }
    });
  }

  async install(): Promise<void> {
    const prompt = this.#installPrompt();
    if (!prompt) return;

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;

      if (outcome === 'accepted') {
        this.#installPrompt.set(null);
        this.#updateInstallableState();
      }
    } catch (error) {
      console.error('PWA install failed:', error);
    }
  }

  async showIOSInstructions() {
    const { PwaInstallDialog } = await import('@tvd/ui/pwa-install-dialog');
    this.#dialog.create(PwaInstallDialog).open();
  }

  #detectIOS(): boolean {
    const userAgent = this.#window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    return isIOS && isSafari;
  }

  #updateInstallableState(): void {
    const hasPrompt = !!this.#installPrompt();
    const isStandalone = this.#isStandalone();
    const isIOSNotInstalled = this.#isIOSDevice && !isStandalone;

    this.isInstallable.set((hasPrompt || isIOSNotInstalled) && !isStandalone);
  }

  async #showAutoToast(): Promise<void> {
    const hasShown = localStorage.getItem(PWA_TOAST_SHOWN_KEY);
    if (hasShown) return;

    const { PwaInstallToast } = await import('@tvd/ui/pwa-install-toast');

    setTimeout(() => {
      this.#toast.show(PwaInstallToast, {
        autoClose: false,
        style: {
          maxWidth: '400px',
          paddingInlineStart: '0',
        },
        data: {
          onInstall: () => {
            if (this.isIOS()) {
              this.showIOSInstructions();
            } else {
              this.install();
            }
          },
        },
      });
      localStorage.setItem(PWA_TOAST_SHOWN_KEY, 'true');
    }, 2000);
  }
}
